import { compressImage } from "@/imageOpt";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth, canSeeCosts } from "@/auth";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { photoUrl } from "@/api";
import CountrySwitcher from "@/components/CountrySwitcher";
import ElevateModal from "@/components/ElevateModal";
import { PATH_SECTION } from "@/sections";
import { FileClock, QrCode, RotateCw, HelpCircle, EyeOff, ArchiveRestore, Sandwich, ExternalLink, CornerDownLeft, Flag, Flame, ShoppingBag, Images, History, Link2, Star, Sunset, Warehouse, FileSpreadsheet } from "lucide-react";
import { LayoutDashboard, ChefHat, Package, ReceiptText, ClipboardList, Users, BarChart3, LogOut, TrendingDown, TrendingUp, ShoppingCart, Bell, CreditCard, Clock, CalendarRange, CalendarDays, CalendarCheck, FolderArchive, FlaskConical, BookOpen, BookImage, Wallet, Calculator, Building2, Lock, Factory, ImagePlus, ArrowUpDown, ChevronUp, ChevronDown, ChevronsLeft, ChevronsRight, Settings, BookMarked, Trash2, Unlock, Truck, Search, Boxes, Sparkles, MessageSquareText, Croissant, Wheat, Layers, Pencil, Plus, Tags, Repeat, Cake, Gift, Moon, CalendarClock } from "lucide-react";
import { askPrompt, askConfirm } from "@/components/ConfirmHost";
import { DedupScanner } from "@/components/DedupScanner";
import { openGlobalSearch } from "@/components/GlobalSearch";
import UndoButton from "@/components/UndoButton";
import NetworkShopModal from "@/components/NetworkShopModal";
import CustomMenuModal from "@/components/CustomMenuModal";

function ElevTimer({ until, onExpire, t }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(i); }, []);
  const leftMs = until * 1000 - now;
  useEffect(() => { if (leftMs <= 0) onExpire?.(); }, [leftMs, onExpire]);
  if (leftMs <= 0) return null;
  const min = Math.max(1, Math.ceil(leftMs / 60000));
  return (
    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-300" data-testid="elev-timer">
      <Unlock className="w-3 h-3" /> {t("elevLeft")} · {min} min
    </div>
  );
}
import LangSwitcher from "@/components/LangSwitcher";
import PushToggle from "@/components/PushToggle";
import ReportProblem from "@/components/ReportProblem";
import NumInput from "@/components/NumInput";

const SECMAP = { "/app/cassa": "cassa", "/app/recipes": "recipes", "/app/inventory": "inventory", "/app/lab": "lab", "/app/ingredients-list": "inglist", "/app/catalog": "catalog", "/app/reorder": "reorder", "/app/waste": "waste", "/app/staff": "staff", "/app/shifts": "staff", "/app/attendance": "staff", "/app/invoices": "invoices", "/app/documents": "documents", "/app/payments": "payments", "/app/orders": "orders", "/app/partners": "partners", "/app/suppliers": "partners", "/app/stats": "stats", "/app/reports": "reports", "/app/azienda": "azienda", "/app/billing": "billing" };

export default function Sidebar({ onNavigate }) {
  const { user, logout, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [notices, setNotices] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const logoRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [navOrder, setNavOrder] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const overKeyRef = useRef(null);
  const pressTimer = useRef(null);
  const pressStart = useRef(null);
  const suppressClick = useRef(false);
  const navScrollRef = useRef(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [elevOpen, setElevOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const restricted = !!user?.allowed_sections;

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLogoBusy(true);
    try { await api.uploadBusinessLogo(await compressImage(f, 800, 0.8)); refresh(); } catch {}
    setLogoBusy(false);
  };

  const [netLinks, setNetLinks] = useState([]);
  const [dsPending, setDsPending] = useState([]);
  const [soChanges, setSoChanges] = useState(0);
  const [netShop, setNetShop] = useState(null);
  useEffect(() => {
    api.priceAlerts().then(setAlerts).catch(() => {});
    api.expiringLots(7).then(setExpiring).catch(() => {});
    api.priceNotices().then(setNotices).catch(() => {});
    api.networkLinks().then((r) => setNetLinks((r.links || []).filter((l) => l.status === "active"))).catch(() => {});
    const loadDsPending = () => api.dsPending().then((r) => setDsPending(r.pending || [])).catch(() => {});
    loadDsPending();
    const loadSoChanges = () => api.standingChanges("pending").then((r) => setSoChanges((r || []).length)).catch(() => {});
    loadSoChanges();
    window.addEventListener("ds-pending-changed", loadDsPending);
    window.addEventListener("so-changes-changed", loadSoChanges);
    return () => { window.removeEventListener("ds-pending-changed", loadDsPending); window.removeEventListener("so-changes-changed", loadSoChanges); };
  }, []);

  const unreadNotices = notices.filter((n) => !n.read).length;
  const openAlerts = () => {
    const next = !showAlerts;
    setShowAlerts(next);
    if (next && unreadNotices > 0) {
      api.priceNoticesReadAll().catch(() => {});
      setNotices((p) => p.map((x) => ({ ...x, read: true })));
    }
  };
  const saveThr = async (e) => {
    const v = parseFloat(e.target.value);
    if (!(v >= 0)) return;
    try { await api.updateBusinessProfile({ price_alert_pct: v }); toast.success(t("saved")); refresh(); } catch {}
  };

  const [trashCount, setTrashCount] = useState(0);
  useEffect(() => {
    const loadTrash = () => api.trashCount().then((r) => setTrashCount(r.count)).catch(() => {});
    loadTrash();
    window.addEventListener("trash-changed", loadTrash);
    return () => window.removeEventListener("trash-changed", loadTrash);
  }, []);

  const [msgUnread, setMsgUnread] = useState(0);
  useEffect(() => {
    const loadUnread = () => api.threadsUnread().then((m) => setMsgUnread(Object.values(m || {}).reduce((s, n) => s + n, 0))).catch(() => {});
    loadUnread();
    const iv = setInterval(loadUnread, 60000);
    return () => clearInterval(iv);
  }, []);

  const [piDue, setPiDue] = useState(false);
  useEffect(() => {
    const loadPi = () => api.piStatus().then((s) => setPiDue(!!s.due)).catch(() => {});
    loadPi();
    const iv = setInterval(loadPi, 3600000);
    return () => clearInterval(iv);
  }, []);

  const baseItems = [
    { to: "/app/cassa", icon: Calculator, label: t("posTitle"), tid: "nav-cassa" },
    { to: "/app/prenotazioni", icon: CalendarCheck, label: t("resvTitle"), tid: "nav-prenotazioni" },
    { to: "/app", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "nav-dashboard" },
    { to: "/app/orders?dept=all", icon: ClipboardList, label: t("ordersAllLbl"), tid: "nav-orders-all" },
    { to: "/app/production", icon: Factory, label: t("navProduction"), tid: "nav-production" },
    { to: "/app/partners", icon: Users, label: t("customersTab"), tid: "nav-partners" },
    { to: "/app/suppliers", icon: Truck, label: t("suppliersTab"), tid: "nav-suppliers" },
    { to: "/app/orders", icon: ClipboardList, label: t("orders"), tid: "nav-orders" },
    { to: "/app/caldane", icon: Repeat, label: t("caldaneNav"), tid: "nav-caldane" },
    { to: "/app/ricorrenti", icon: CalendarClock, label: t("recurNav"), tid: "nav-ricorrenti" },
    { to: "/app/torte", icon: Cake, label: t("torteNav"), tid: "nav-torte" },
    { to: "/app/panettoni", icon: Gift, label: t("panTitle"), tid: "nav-panettoni" },
    { to: "/app/avanzi", icon: Moon, label: t("avanziNav"), tid: "nav-avanzi" },
    { to: "/app/waste", icon: TrendingDown, label: t("waste"), tid: "nav-waste" },
    { to: "/app/preventivi", icon: FileSpreadsheet, label: t("quotesTitle"), tid: "nav-preventivi" },
    { to: "/app/b2b", icon: Building2, label: t("b2bTitle"), tid: "nav-b2b" },
    { to: "/app/messages", icon: MessageSquareText, label: t("navMessages"), tid: "nav-messages" },
    { to: "/app/recipes", icon: ChefHat, label: t("recipes"), tid: "nav-recipes" },
    { to: "/app/formula", icon: BookMarked, label: t("formulaTitle"), tid: "nav-formula" },
    { to: "/app/lab", icon: FlaskConical, label: t("lab"), tid: "nav-lab" },
    { to: "/app/inventory", icon: Package, label: t("inventory"), tid: "nav-inventory" },
    { to: "/app/inventario", icon: Boxes, label: t("invTitle"), tid: "nav-inventario" },
    { to: "/app/storico-produzione", icon: History, label: t("phTitle"), tid: "nav-prodhistory" },
    { to: "/app/devai", icon: Sparkles, label: "Developer AI", tid: "nav-devai" },
    { to: "/app/ingredients-list", icon: ClipboardList, label: t("ingredientsList"), tid: "nav-inglist" },
    { to: "/app/catalog", icon: BookImage, label: t("catalogLbl"), tid: "nav-catalog" },
    { to: "/app/foto", icon: Images, label: t("photosLbl"), tid: "nav-foto" },
    { to: "/app/listino", icon: Tags, label: t("listinoTitle"), tid: "nav-listino" },
    { to: "/app/foodcost", icon: Sandwich, label: t("foodCostTitle"), tid: "nav-foodcost" },
    { to: "/app/acquistati", icon: ShoppingBag, label: t("purchTitle"), tid: "nav-acquistati" },
    { to: "/app/eventi", icon: Flag, label: t("specialTitle"), tid: "nav-eventi" },
    { to: "/app/reorder", icon: ShoppingCart, label: t("reorderTitle"), tid: "nav-reorder" },
    { to: "/app/shifts", icon: CalendarRange, label: t("shifts"), tid: "nav-shifts" },
    { to: "/app/azienda", icon: Building2, label: t("azienda"), tid: "nav-azienda" },
    { to: "/app/attendance", icon: CalendarDays, label: t("attendance"), tid: "nav-attendance" },
    { to: "/app/invoices", icon: ReceiptText, label: t("invoices"), tid: "nav-invoices" },
    { to: "/app/past-docs", icon: FileClock, label: t("pdBolleNav"), tid: "nav-pastdocs" },
    { to: "/app/past-docs?cat=caldane", icon: Flame, label: t("pdCaldaneNav"), tid: "nav-pastdocs-caldane" },
    { to: "/app/labels-history", icon: QrCode, label: t("lblHistTitle"), tid: "nav-labels-history" },
    { to: "/app/staff", icon: Clock, label: t("staff"), tid: "nav-staff" },
    { to: "/app/documents", icon: FolderArchive, label: t("documents"), tid: "nav-documents" },
    { to: "/app/payments", icon: Wallet, label: t("payments"), tid: "nav-payments" },
    { to: "/app/stats", icon: TrendingUp, label: t("liveStats"), tid: "nav-stats" },
    { to: "/app/reports", icon: BarChart3, label: t("reports"), tid: "nav-reports" },
    { to: "/app/billing", icon: CreditCard, label: t("billing"), tid: "nav-billing" },
    { to: "/app/guide", icon: BookOpen, label: t("guide"), tid: "nav-guide" },
    { to: "/app/trash", icon: Trash2, label: t("navTrash"), tid: "nav-trash" },
  ].map((it) => (user?.nav_labels?.[it.to] ? { ...it, label: user.nav_labels[it.to] } : it));
  const navLabels = user?.nav_labels || {};

  const savedOrder = navOrder || user?.nav_order;
  const ordered = (savedOrder && savedOrder.length)
    ? [...baseItems].sort((a, b) => {
        const pos = (x) => {
          const i = savedOrder.indexOf(x.to);
          if (i !== -1) return i;
          if (x.to === "/app/orders?dept=all") {
            const d = savedOrder.indexOf("/app");
            return (d !== -1 ? d : -1) + 0.5;
          }
          return savedOrder.length + baseItems.indexOf(x);
        };
        return pos(a) - pos(b);
      })
    : baseItems;
  const hiddenNav = user?.hidden_nav || [];
  const [archOpen, setArchOpen] = useState(false);
  const [editNav, setEditNav] = useState(false);
  const hideNavItem = async (key) => {
    try {
      if (restricted) await api.saveNavPrefs({ hidden: [...hiddenNav, key] });
      else await api.updateBusinessProfile({ hidden_nav: [...hiddenNav, key] });
      toast.success(t("navArchivedOk")); refresh();
    } catch { toast.error("Errore"); }
  };
  const restoreNavItem = async (key) => {
    try {
      if (restricted) await api.saveNavPrefs({ hidden: hiddenNav.filter((k) => k !== key) });
      else await api.updateBusinessProfile({ hidden_nav: hiddenNav.filter((k) => k !== key) });
      toast.success(t("navRestoredOk")); refresh();
    } catch { toast.error("Errore"); }
  };
  const delDept = async (id) => {
    const name = departments.find((d) => d.id === id)?.name || id;
    let cnt = 0;
    try { cnt = ((await api.recipes(id)) || []).length; } catch { /* ignore */ }
    let msg = t("deptDelConfirm").replace("{name}", name);
    if (cnt > 0) msg += " " + t("deptDelMove").replace("{n}", cnt);
    if (!(await askConfirm(msg))) return;
    try {
      await api.deleteDepartment(id);
      toast.success(t("deptDelOk"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const items = (restricted ? ordered.filter((it) => user.allowed_sections.includes(PATH_SECTION[it.to])) : ordered).filter((it) => !hiddenNav.includes(it.to));

  // ---- Custom menu sections (sezioni/voci personalizzate con collegamenti) ----
  const customMenu = user?.custom_menu || [];
  const [cmModal, setCmModal] = useState(null);
  const [cmOpen, setCmOpen] = useState(() => { try { return JSON.parse(localStorage.getItem("sidebar_cmopen") || "{}"); } catch { return {}; } });
  const toggleCm = (id) => setCmOpen((o) => { const n = { ...o, [id]: o[id] === false ? true : false }; localStorage.setItem("sidebar_cmopen", JSON.stringify(n)); return n; });
  const saveCM = async (next) => {
    try { await api.updateBusinessProfile({ custom_menu: next }); refresh(); toast.success(t("saved")); }
    catch { toast.error("Errore"); }
  };
  const moveArr = (arr, i, dir) => { const j = i + dir; if (j < 0 || j >= arr.length) return arr; const n = [...arr]; [n[i], n[j]] = [n[j], n[i]]; return n; };
  const cmMap = (sid, fn) => customMenu.map((s) => (s.id === sid ? { ...s, items: fn(s.items || []) } : s));
  const addCmSection = async () => {
    const v = await askPrompt(t("cmNewSectionPh"));
    if (!v || !v.trim()) return;
    saveCM([...customMenu, { id: Date.now().toString(36), title: v.trim().slice(0, 30), items: [] }]);
  };
  const renameCmSection = async (s) => {
    const v = await askPrompt(t("navRenameTip"), s.title);
    if (!v || !v.trim()) return;
    saveCM(customMenu.map((x) => (x.id === s.id ? { ...x, title: v.trim().slice(0, 30) } : x)));
  };
  const delCmSection = async (s) => {
    if (!(await askConfirm(t("cmDelSectionConfirm").replace("{name}", s.title)))) return;
    saveCM(customMenu.filter((x) => x.id !== s.id));
  };
  const delCmItem = async (sid, it) => {
    if (!(await askConfirm(t("cmDelItemConfirm").replace("{name}", it.label)))) return;
    saveCM(cmMap(sid, (arr) => arr.filter((x) => x.id !== it.id)));
  };
  const onCmSave = (sectionId, itm) => {
    saveCM(cmMap(sectionId, (arr) => (arr.some((x) => x.id === itm.id) ? arr.map((x) => (x.id === itm.id ? itm : x)) : [...arr, itm])));
    setCmModal(null);
  };
  const CM_ICON = { page: ExternalLink, client: Users, supplier: Truck, employee: Clock, formula: BookMarked, catalog: BookImage, ingredient: Package };

  const RESERVED = ["/app/azienda", "/app/attendance", "/app/invoices", "/app/past-docs", "/app/staff", "/app/documents", "/app/payments", "/app/stats", "/app/reports", "/app/billing"];
  const DEPT_PATHS_ALL = ["/app/formula", "/app/recipes", "/app/inventario", "/app/storico-produzione", "/app/orders", "/app/catalog", "/app/listino", "/app/foto"];
  const extracted = (user?.nav_extracted || []).filter((k) => DEPT_PATHS_ALL.includes(k.split("@")[0]));
  const DEPT_PATHS = DEPT_PATHS_ALL.filter((p) => !extracted.includes(p));
  const departments = user?.departments?.length ? user.departments : [{ id: "bakery", name: "Panificazione" }, { id: "pasticceria", name: "Pasticceria" }];
  const deptItems = items.filter((it) => DEPT_PATHS.includes(it.to));
  const visibleDepts = departments.filter((d) => !hiddenNav.includes(`group:${d.id}`));
  const deptNameOf = (id) => departments.find((x) => x.id === id)?.name || id;
  const xDefaultLabel = (k) => {
    const [p, dep] = k.split("@");
    return `${baseItems.find((i) => i.to === p)?.label || ""} ${deptNameOf(dep)}`.trim();
  };
  const extractedEntries = extracted.filter((k) => k.includes("@")).map((k) => {
    const [p, dep] = k.split("@");
    const base = baseItems.find((i) => i.to === p);
    if (!base || !departments.some((x) => x.id === dep)) return null;
    if (restricted && !user.allowed_sections.includes(PATH_SECTION[p])) return null;
    return { key: k, type: "item", it: { ...base, to: dep === "bakery" ? p : `${p}?dept=${dep}`,
      label: navLabels[k] || xDefaultLabel(k), tid: `nav-x-${base.tid}-${dep}` } };
  }).filter(Boolean).filter((e) => !hiddenNav.includes(e.key));
  const loc = useLocation();
  const [deptOpen, setDeptOpen] = useState(() => { try { return JSON.parse(localStorage.getItem("sidebar_deptopen") || "{}"); } catch { return {}; } });
  const toggleDept = (id) => setDeptOpen((o) => { const n = { ...o, [id]: o[id] === false ? true : false }; localStorage.setItem("sidebar_deptopen", JSON.stringify(n)); return n; });
  const renameDept = async (d) => {
    const v = await askPrompt(t("deptRenameTip"), d.name);
    if (!v || !v.trim()) return;
    try {
      await api.updateBusinessProfile({ departments: departments.map((x) => x.id === d.id ? { ...x, name: v.trim().slice(0, 30) } : x) });
      refresh(); toast.success(t("saved"));
    } catch { toast.error("Errore"); }
  };
  const addDept = async () => {
    const v = await askPrompt(t("deptNewPh"));
    if (!v || !v.trim()) return;
    const id = v.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 24) || `rep-${Date.now().toString(36)}`;
    if (departments.some((d) => d.id === id)) { toast.error(t("deptExists")); return; }
    try {
      await api.updateBusinessProfile({ departments: [...departments, { id, name: v.trim().slice(0, 30) }] });
      refresh(); toast.success(t("saved"));
    } catch { toast.error("Errore"); }
  };
  const deptLink = (it, d) => {
    const to = d.id === "bakery" ? it.to : `${it.to}?dept=${d.id}`;
    const curDept = new URLSearchParams(loc.search).get("dept") || "bakery";
    const active = loc.pathname === it.to && curDept === d.id;
    return (
      <NavLink key={`${d.id}-${it.to}`} to={to} data-testid={`nav-dept-${d.id}-${it.tid}`} title={it.label}
        onClick={() => onNavigate?.()}
        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${collapsed ? "justify-center" : ""} ${active ? "bg-white/10 text-white font-semibold shadow-[inset_2px_0_0_#C5A880]" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
        <it.icon className="w-4 h-4 shrink-0" />
        {!collapsed && it.label}
        {it.to === "/app/orders" && soChanges > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" data-testid={`so-chg-dot-${d.id}`} />}
      </NavLink>
    );
  };
  const freeItems = items.filter((it) => !RESERVED.includes(it.to) && !DEPT_PATHS.includes(it.to));
  const reservedItems = items.filter((it) => RESERVED.includes(it.to));
  const [bizOpen, setBizOpen] = useState(() => localStorage.getItem("sidebar_bizopen") === "1");
  const bizGroupClick = () => {
    if (restricted && !user?.staff?.elevated && reservedItems.length === 0) { setElevOpen(true); return; }
    const v = !bizOpen; setBizOpen(v); localStorage.setItem("sidebar_bizopen", v ? "1" : "0");
  };

  const groupEntriesBase = [
    ...(deptItems.length > 0 ? visibleDepts.map((d) => ({ key: `group:${d.id}`, type: "dept", d })) : []),
    ...((reservedItems.length > 0 && !hiddenNav.includes("group:azienda")) ? [{ key: "group:azienda", type: "azienda" }] : []),
  ];
  const entryList = [...freeItems.map((it) => ({ key: it.to, type: "item", it })), ...extractedEntries, ...groupEntriesBase];
  const orderedEntries = (savedOrder?.length)
    ? entryList.map((e, i) => {
        const idx = savedOrder.indexOf(e.key);
        if (idx !== -1) return [idx, i, e];
        if (e.key === "/app/orders?dept=all") {
          const d = savedOrder.indexOf("/app");
          return [(d !== -1 ? d : -1) + 0.5, i, e];
        }
        return [savedOrder.length + i, i, e];
      }).sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((x) => x[2])
    : entryList;

  const applyDrag = () => {
    const topKeys = orderedEntries.map((e) => e.key);
    const dKeys = deptItems.map((i) => i.to);
    const rKeys = reservedItems.map((i) => i.to);
    const moveIn = (arr) => {
      const from = arr.indexOf(dragKey);
      const to = arr.indexOf(overKeyRef.current);
      if (from >= 0 && to >= 0 && from !== to) { arr.splice(to, 0, arr.splice(from, 1)[0]); return true; }
      return false;
    };
    let changed = false;
    if (topKeys.includes(dragKey)) changed = moveIn(topKeys);
    else if (rKeys.includes(dragKey)) changed = moveIn(rKeys);
    else if (dKeys.includes(dragKey)) changed = moveIn(dKeys);
    if (changed) {
      const keys = [...topKeys, ...dKeys, ...rKeys];
      setNavOrder(keys);
      api.saveNavOrder(keys).catch(() => {});
    }
    if (dragKey) {
      suppressClick.current = true;
      setTimeout(() => { suppressClick.current = false; }, 350);
    }
    setDragKey(null); setOverKey(null); overKeyRef.current = null;
  };

  const moveKey = (key, dir) => {
    const topKeys = orderedEntries.map((e) => e.key);
    const dKeys = deptItems.map((i) => i.to);
    const rKeys = reservedItems.map((i) => i.to);
    const arr = topKeys.includes(key) ? topKeys : rKeys.includes(key) ? rKeys : dKeys.includes(key) ? dKeys : null;
    if (!arr) return;
    const i = arr.indexOf(key);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    const keys = [...topKeys, ...dKeys, ...rKeys];
    setNavOrder(keys);
    api.saveNavOrder(keys).catch(() => {});
  };

  const toggleExtract = async (key) => {
    const next = extracted.includes(key) ? extracted.filter((k) => k !== key) : [...extracted, key];
    try { await api.updateBusinessProfile({ nav_extracted: next }); refresh(); toast.success(t("saved")); }
    catch { toast.error("Errore"); }
  };
  const renameNav = async (key) => {
    if (key.startsWith("group:") && key !== "group:azienda") {
      if (restricted) { toast.info(t("navOwnerOnlyGroup")); return; }
      const d = departments.find((x) => `group:${x.id}` === key);
      if (d) renameDept(d);
      return;
    }
    const cur = key === "group:azienda" ? (navLabels[key] || t("aziendaGroupLbl"))
      : key.includes("@") ? (navLabels[key] || xDefaultLabel(key))
      : (baseItems.find((i) => i.to === key)?.label || "");
    const v = await askPrompt(t("navRenameTip"), cur);
    if (!v || !v.trim()) return;
    try {
      if (restricted) await api.saveNavPrefs({ labels: { ...navLabels, [key]: v.trim().slice(0, 30) } });
      else await api.updateBusinessProfile({ nav_labels: { ...navLabels, [key]: v.trim().slice(0, 30) } });
      refresh(); toast.success(t("saved"));
    } catch { toast.error("Errore"); }
  };

  const moveArrows = (key, extractKey) => (!collapsed && editNav ? (
    <div className="flex flex-col justify-center shrink-0" onPointerDown={(e) => e.stopPropagation()}>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveKey(key, -1); }} className="text-white/25 hover:text-white p-0.5 leading-none" data-testid={`nav-up-${key.replace(/[/:]/g, "-")}`}><ChevronUp className="w-3 h-3" /></button>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); renameNav(key); }} className="text-white/25 hover:text-[#C5A880] p-0.5 leading-none" title={t("navRenameTip")} data-testid={`nav-ren-${key.replace(/[/:]/g, "-")}`}><Pencil className="w-3 h-3" /></button>
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); hideNavItem(extractKey && !extracted.includes(extractKey) ? extractKey : key); }} className="text-white/25 hover:text-amber-400 p-0.5 leading-none" title={t("navArchBtn")} data-testid={`nav-arch-${(extractKey && !extracted.includes(extractKey) ? extractKey : key).replace(/[/:@]/g, "-")}`}><EyeOff className="w-3 h-3" /></button>
      {!restricted && extractKey && !extracted.includes(extractKey) && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExtract(extractKey); }} className="text-white/25 hover:text-sky-400 p-0.5 leading-none" title={t("navExtractTip")} data-testid={`nav-extract-${extractKey.replace(/[/:@]/g, "-")}`}><ExternalLink className="w-3 h-3" /></button>
      )}
      {!restricted && (key.includes("@") || DEPT_PATHS_ALL.includes(key)) && extracted.includes(key) && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExtract(key); }} className="text-white/25 hover:text-sky-400 p-0.5 leading-none" title={t("navRegroupTip")} data-testid={`nav-regroup-${key.replace(/[/:@]/g, "-")}`}><CornerDownLeft className="w-3 h-3" /></button>
      )}
      {!restricted && key.startsWith("group:") && !["group:azienda", "group:bakery", "group:pasticceria"].includes(key) && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); delDept(key.slice(6)); }} className="text-white/25 hover:text-red-400 p-0.5 leading-none" title={t("deptDelBtn")} data-testid={`nav-dept-del-${key.slice(6)}`}><Trash2 className="w-3 h-3" /></button>
      )}
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveKey(key, 1); }} className="text-white/25 hover:text-white p-0.5 leading-none" data-testid={`nav-down-${key.replace(/[/:]/g, "-")}`}><ChevronDown className="w-3 h-3" /></button>
    </div>
  ) : null);

  useEffect(() => {
    if (!dragKey) return;
    const onMove = (e) => {
      const pt = e.touches ? e.touches[0] : e;
      const navEl = navScrollRef.current;
      if (navEl) {
        const r = navEl.getBoundingClientRect();
        if (pt.clientY < r.top + 60) navEl.scrollTop -= 14;
        else if (pt.clientY > r.bottom - 60) navEl.scrollTop += 14;
      }
      const el = document.elementFromPoint(pt.clientX, pt.clientY)?.closest?.("[data-navkey]");
      if (el) { const k = el.getAttribute("data-navkey"); overKeyRef.current = k; setOverKey(k); }
    };
    const onTouch = (e) => { e.preventDefault(); onMove(e); };
    const end = () => applyDrag();
    window.addEventListener("pointermove", onMove);
    window.addEventListener("touchmove", onTouch, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("touchend", end);
    };
  }, [dragKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPress = (key, e) => {
    if (restricted) return;
    pressStart.current = { x: e.clientX, y: e.clientY };
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      overKeyRef.current = key;
      setDragKey(key);
      setOverKey(key);
      try { navigator.vibrate?.(25); } catch {}
    }, 350);
  };
  const checkMove = (e) => {
    if (dragKey || !pressStart.current) return;
    if (Math.abs(e.clientX - pressStart.current.x) > 8 || Math.abs(e.clientY - pressStart.current.y) > 8) {
      clearTimeout(pressTimer.current);
      pressStart.current = null;
    }
  };
  const cancelPress = () => { clearTimeout(pressTimer.current); pressStart.current = null; };

  const subWrap = (key, child, extractKey) => (
    <div key={extractKey || key} data-navkey={key}
      onPointerDown={(e) => { e.stopPropagation(); startPress(key, e); }}
      onPointerMove={checkMove}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onClickCapture={(e) => { if (suppressClick.current || dragKey) { e.preventDefault(); e.stopPropagation(); } }}
      className={`select-none flex items-stretch ${dragKey === key ? "opacity-40 scale-[0.98]" : ""} ${overKey === key && dragKey && dragKey !== key ? "outline outline-2 outline-[#C5A880] rounded-xl" : ""} ${dragKey ? "cursor-grabbing" : ""}`}
      data-testid={`nav-subentry-${key.replace(/[/:]/g, "-")}`}>
      <div className="flex-1 min-w-0">{child}</div>
      {moveArrows(key, extractKey)}
    </div>
  );

  const toggleCollapse = () => {
    const v = !collapsed;
    setCollapsed(v);
    localStorage.setItem("sidebar_collapsed", v ? "1" : "0");
  };

  const renderDeptGroup = (d) => {
    const DeptIcon = d.id === "pasticceria" ? Croissant : d.id === "bakery" ? Wheat : Layers;
    const open = deptOpen[d.id] !== false;
    return (
      <div className="flex flex-col gap-1">
        <div onClick={() => toggleDept(d.id)} title={d.name} data-testid={`nav-dept-group-${d.id}`}
          className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-colors ${collapsed ? "justify-center" : ""} ${open ? "bg-white/10 text-white font-semibold" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
          <DeptIcon className="w-4 h-4 shrink-0" />
          {!collapsed && <span className="flex-1 text-left truncate">{d.name}</span>}
          {!collapsed && !restricted && (
            <button onClick={(e) => { e.stopPropagation(); renameDept(d); }} title={t("deptRenameTip")} data-testid={`dept-rename-${d.id}`}
              className="p-0.5 rounded hover:bg-white/15 opacity-40 group-hover:opacity-90 transition-opacity">
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {!collapsed && (open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
        </div>
        {open && (
          <div className={`flex flex-col gap-1 ${collapsed ? "" : "ml-3 pl-2 border-l border-white/10"}`} data-testid={`nav-dept-items-${d.id}`}>
            {deptItems.filter((it) => !extracted.includes(`${it.to}@${d.id}`) && !hiddenNav.includes(`${it.to}@${d.id}`)).map((it) => subWrap(it.to, deptLink(it, d), `${it.to}@${d.id}`))}
            {(!restricted || user?.allowed_sections?.includes("inventory")) && [
              { tab: "order", icon: ShoppingBag, k: "dsOrderNav" },
              { tab: "stock", icon: Warehouse, k: "dsStockNav" },
              ...(canSeeCosts(user) ? [{ tab: "prices", icon: Tags, k: "dsPricesNav" }] : []),
              { tab: "docs", icon: FileClock, k: "dsDocsNav" },
              { tab: "eod", icon: Sunset, k: "dsEodNav" },
            ].filter((l) => !hiddenNav.includes(`ds:${d.id}:${l.tab}`)).map((l) => {
              const dsActive = loc.pathname === `/app/dept-shop/${d.id}` && (new URLSearchParams(loc.search).get("tab") || "order") === l.tab;
              return (
                <div key={l.tab} className="flex items-stretch">
                <NavLink to={`/app/dept-shop/${d.id}?tab=${l.tab}`} onClick={() => onNavigate?.()} title={t(l.k)} data-testid={`nav-ds-${d.id}-${l.tab}`}
                  className={`flex-1 min-w-0 relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${collapsed ? "justify-center" : ""} ${dsActive ? "bg-white/10 text-white font-semibold shadow-[inset_2px_0_0_#C5A880]" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                  <l.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="flex-1 truncate text-left">{t(l.k)}</span>}
                  {l.tab === "eod" && dsPending.includes(d.id) && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" data-testid={`ds-eod-dot-${d.id}`} />}
                </NavLink>
                {!collapsed && editNav && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); hideNavItem(`ds:${d.id}:${l.tab}`); }}
                    className="text-white/25 hover:text-amber-400 px-1 leading-none shrink-0" title={t("navArchBtn")} data-testid={`nav-arch-ds-${d.id}-${l.tab}`}>
                    <EyeOff className="w-3 h-3" />
                  </button>
                )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAziendaGroup = () => (
    <div className="flex flex-col gap-1">
      <button onClick={bizGroupClick}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${collapsed ? "justify-center" : ""} ${bizOpen ? "bg-white/10 text-white font-semibold" : "text-white/60 hover:bg-white/5 hover:text-white"}`}
        title={navLabels["group:azienda"] || t("aziendaGroupLbl")} data-testid="nav-azienda-group">
        <Building2 className="w-4 h-4 shrink-0" />
        {!collapsed && <span className="flex-1 text-left">{navLabels["group:azienda"] || t("aziendaGroupLbl")}</span>}
        {!collapsed && (restricted && !user?.staff?.elevated ? (
          <Lock className="w-3.5 h-3.5 text-[#C5A880]" data-testid="azienda-group-lock" />
        ) : bizOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
      </button>
      {bizOpen && reservedItems.length > 0 && (
        <div className={`flex flex-col gap-1 ${collapsed ? "" : "ml-3 pl-2 border-l border-white/10"}`} data-testid="nav-azienda-items">
          {reservedItems.map((it) => subWrap(it.to, renderItem(it)))}
        </div>
      )}
    </div>
  );

  const renderItem = (it) => {
    return (
      <NavLink
        key={it.to}
        to={it.to}
        end={it.end}
        data-testid={it.tid}
        title={it.label}
        draggable={false}
        onClick={() => { onNavigate?.(); }}
        className={({ isActive }) => {
          const qi = it.to.indexOf("?");
          const active = qi >= 0
            ? `${loc.pathname}${loc.search}` === it.to
            : isActive && !(it.to === "/app/past-docs" && loc.search.includes("cat=caldane"));
          return `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${collapsed ? "justify-center" : ""} ${
            active ? "bg-white/10 text-white font-semibold shadow-[inset_2px_0_0_#C5A880]" : "text-white/60 hover:bg-white/5 hover:text-white"
          }`;
        }}
      >
        <it.icon className="w-4 h-4 shrink-0" />
        {!collapsed && it.label}
        {it.to.startsWith("/app/orders") && soChanges > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0 ml-1" data-testid={`so-chg-dot-${it.tid}`} />}
        {it.to === "/app/inventory" && piDue && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0 ml-1" title={t("piDueDot")} data-testid="pi-due-dot" />}
        {it.to === "/app/trash" && trashCount > 0 ? (
          <span className={`${collapsed ? "absolute top-0.5 right-0.5" : "ml-auto"} bg-[#e74c3c] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shrink-0`} data-testid="trash-count-badge">
            {trashCount}
          </span>
        ) : it.to === "/app/messages" && msgUnread > 0 ? (
          <span className={`${collapsed ? "absolute top-0.5 right-0.5" : "ml-auto"} bg-[#e74c3c] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shrink-0`} data-testid="messages-count-badge">
            {msgUnread}
          </span>
        ) : (
          user?.has_lock && (user?.locked_sections || []).includes(SECMAP[it.to]) && <Lock className="w-3 h-3 ml-auto text-white/35 shrink-0" data-testid={`nav-lock-${SECMAP[it.to]}`} />
        )}
      </NavLink>
    );
  };

  return (
    <aside className={`${collapsed ? "w-20" : "w-64"} shrink-0 h-screen sticky top-0 flex flex-col text-white z-40 transition-all duration-200`} style={{ background: "linear-gradient(180deg, hsl(216 65% 12%), hsl(216 60% 9%))" }}>
      {/* Top controls: lang · country · bell · collapse */}
      <div className="px-3 pt-3 relative" data-testid="sidebar-top-controls">
        <div className={`flex items-center gap-1 ${collapsed ? "justify-center" : ""}`}>
          {!collapsed && (
            <div className="flex-1 min-w-0 flex items-center flex-wrap gap-y-1 gap-x-0.5 text-white/80 [&_svg]:text-white/60 text-xs">
              <LangSwitcher />
              {!restricted && <CountrySwitcher dark />}
              <button
                onClick={openGlobalSearch}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="sidebar-search-btn"
                title={t("globalSearchBtn")}
              >
                <Search className="w-4 h-4 text-white/70" />
              </button>
              <DedupScanner />
              <button
                onClick={openAlerts}
                className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="btn-price-alerts"
                title={t("priceAlertsTitle")}
              >
                <Bell className="w-4 h-4 text-white/70" />
                {(alerts.length + expiring.length + unreadNotices) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#C5A880] text-[10px] font-bold text-[#0D1F3D] flex items-center justify-center" data-testid="alerts-count">
                    {alerts.length + expiring.length + unreadNotices}
                  </span>
                )}
              </button>
              <button onClick={() => window.location.reload()} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title={t("reloadPageLbl")} data-testid="sidebar-reload-btn">
                <RotateCw className="w-4 h-4 text-white/70" />
              </button>
              <UndoButton dark />
              <button onClick={() => window.dispatchEvent(new CustomEvent("cc-start-tour"))} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title={t("tourBtnLbl")} data-testid="sidebar-tour-btn">
                <HelpCircle className="w-4 h-4 text-[#C5A880]" />
              </button>
              <button onClick={() => window.dispatchEvent(new CustomEvent("cc-open-gemini"))} className="p-2 rounded-lg hover:bg-white/10 transition-colors" title="Gemini" data-testid="sidebar-gemini-btn">
                <Sparkles className="w-4 h-4 text-[#9b8cff]" />
              </button>
            </div>
          )}
          <button onClick={toggleCollapse}
            className={`hidden md:flex shrink-0 items-center justify-center p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors ${collapsed ? "" : "ml-auto"}`}
            title={collapsed ? t("navExpand") : t("navCollapse")} data-testid="sidebar-collapse-toggle">
            {collapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
          </button>
        </div>
        {showAlerts && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowAlerts(false)} data-testid="alerts-backdrop" />
            <div className="absolute left-3 right-3 top-full mt-1 md:left-full md:right-auto md:top-4 md:ml-2 md:w-80 z-50 bg-card text-foreground border-2 border-border rounded-2xl shadow-2xl p-4" data-testid="alerts-panel">
              <div className="text-sm font-bold mb-2">{t("priceAlertsTitle")}</div>
              {alerts.length === 0 && expiring.length === 0 && notices.length === 0 && <div className="text-sm text-muted-foreground">{t("noAlerts")}</div>}
              {notices.length > 0 && (
                <div className="mb-2" data-testid="price-notices-list">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-destructive mb-1">⚠ {t("priceNoticeTitle")}</div>
                  <ul className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                    {notices.map((n) => (
                      <li key={n.id} className="text-xs border-b border-border/40 pb-1.5 last:border-0" data-testid={`price-notice-${n.id}`}>
                        <span className="font-semibold">{n.name}</span> — € {n.old} → <b className="text-destructive">€ {n.new}</b> <span className="text-destructive font-bold">(+{n.pct}%)</span>
                        <span className="text-muted-foreground"> · {new Date(n.ts).toLocaleDateString()}</span>
                        {!n.applied && (
                          <button onClick={async () => {
                            if (!(await askConfirm(t("noticeApplyConfirm").replace("{name}", n.name).replace("{pct}", n.pct)))) return;
                            try {
                              const r = await api.priceNoticeApply(n.id);
                              toast.success(t("noticeApplyOk").replace("{n}", r.updated.length));
                              api.priceNotices().then(setNotices).catch(() => {});
                            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                          }} className="ml-1.5 pill text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-bold"
                            data-testid={`notice-apply-${n.id}`}>✓ {t("noticeApplyBtn")}</button>
                        )}
                        {n.applied && <span className="ml-1.5 text-[10px] text-emerald-600 font-bold">✓ {t("noticeAppliedLbl")}</span>}
                      </li>
                    ))}
                  </ul>
                  <div className="text-[10px] text-muted-foreground mt-1">{t("noticeKeepHint")}</div>
                </div>
              )}
              {expiring.length > 0 && (
                <div className="mb-2" data-testid="expiry-alerts-list">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-destructive mb-1">{t("expiryAlerts")}</div>
                  <ul className="flex flex-col gap-1.5 max-h-40 overflow-y-auto">
                    {expiring.map((l) => (
                      <li key={l.id} className="text-xs border-b border-border/40 pb-1.5 last:border-0" data-testid={`expiry-alert-${l.id}`}>
                        <span className="font-semibold">{l.item_name}</span> · {l.lot_code}
                        <span className={l.expired ? "text-destructive font-bold" : "text-amber-600"}> — {l.expired ? t("expiredLbl") : l.expiry_date}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <ul className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                {alerts.map((a) => (
                  <li key={a.ingredient_id} className="text-sm border-b border-border/50 pb-2 last:border-0" data-testid={`alert-${a.ingredient_id}`}>
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("cheaperFrom")} <span className="text-accent font-semibold">{a.best_supplier}</span>: € {a.best_price}/{a.unit}
                      <span className="text-destructive"> (−{a.saving_pct}%)</span>
                    </div>
                  </li>
                ))}
              </ul>
              {alerts.length > 0 && (
                <Link to="/app/inventory" onClick={() => { setShowAlerts(false); onNavigate?.(); }} className="block text-xs text-primary link-underline mt-3" data-testid="alerts-goto-inventory">
                  {t("inventory")} →
                </Link>
              )}
              {!restricted && (
                <div className="mt-3 pt-2.5 border-t border-border/50 flex items-center gap-2" data-testid="price-alert-threshold">
                  <span className="text-[11px] text-muted-foreground flex-1 leading-snug">{t("priceNoticeThr")}</span>
                  <NumInput type="number" min="0" max="100" step="1" defaultValue={user?.price_alert_pct ?? 10} onBlur={saveThr}
                    className="w-16 field text-xs text-center py-1" data-testid="price-alert-thr-input" />
                  <span className="text-xs font-semibold">%</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Identity: logo + brand + business name */}
      <div className={collapsed ? "px-3 py-3 flex justify-center" : "px-4 pt-3 pb-4"}>
        <div className="flex items-center gap-3 min-w-0">
          {restricted ? (
            <div className="shrink-0">
              {user?.logo_path ? (
                <img src={photoUrl(user.logo_path)} alt="logo" className="w-12 h-12 rounded-xl object-cover border border-white/20" data-testid="sidebar-business-logo" />
              ) : (
                <div className="w-12 h-12 rounded-xl border border-white/20 flex items-center justify-center">
                  <ChefHat className="w-5 h-5 text-white/50" />
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => logoRef.current?.click()} disabled={logoBusy} className="shrink-0 block group/logo" title={t("logoUploadBtn")} data-testid="sidebar-logo-upload">
              {user?.logo_path ? (
                <img src={photoUrl(user.logo_path)} alt="logo" className={`w-12 h-12 rounded-xl object-cover border border-white/20 group-hover/logo:border-[#C5A880] transition-colors ${logoBusy ? "opacity-50" : ""}`} data-testid="sidebar-business-logo" />
              ) : (
                <div className={`w-12 h-12 rounded-xl border border-dashed border-white/30 group-hover/logo:border-[#C5A880] flex items-center justify-center transition-colors ${logoBusy ? "opacity-50" : ""}`}>
                  <ImagePlus className="w-5 h-5 text-white/50" />
                </div>
              )}
            </button>
          )}
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogoFile} data-testid="sidebar-logo-input" />
          {!collapsed && (
            restricted ? (
              <button
                onClick={() => { if (user?.staff?.elevated) { nav("/app/azienda"); onNavigate?.(); } else setElevOpen(true); }}
                className="block min-w-0 text-left group"
                title={t("elevTitle")}
                data-testid="sidebar-account-link"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C5A880]">Chef Control</div>
                <div className="serif text-lg font-semibold leading-tight truncate group-hover:text-[#C5A880] transition-colors" data-testid="sidebar-business-name">{user?.name}</div>
                <div className="text-xs text-white/50 truncate">{t(user?.type || "restaurant")}{user?.staff?.name ? ` · ${user.staff.name}` : ""}</div>
                {user?.staff?.elevated && user?.staff?.elev_until > 0 && <ElevTimer until={user.staff.elev_until} onExpire={refresh} t={t} />}
              </button>
            ) : (
              <Link to="/app/azienda" onClick={onNavigate} className="block min-w-0 group" title={t("accountTitle")} data-testid="sidebar-account-link">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C5A880]">Chef Control</div>
                <div className="serif text-lg font-semibold leading-tight group-hover:text-[#C5A880] transition-colors truncate" data-testid="sidebar-business-name">{user?.name}</div>
                <div className="text-xs text-white/50 truncate">{t(user?.type || "restaurant")}</div>
              </Link>
            )
          )}
        </div>
      </div>

      <nav ref={navScrollRef} className="px-3 flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 pb-2">
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-wider text-white/30 select-none" data-testid="nav-drag-hint">
            <ArrowUpDown className="w-3 h-3 shrink-0" />
            <span className="flex-1">{t("navDragHint")}</span>
            <button onClick={() => { const v = !editNav; setEditNav(v); if (v) setArchOpen(true); }}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg normal-case tracking-normal text-[11px] font-semibold transition-colors shrink-0 ${editNav ? "bg-[#C5A880] text-[#0D1F3D]" : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"}`}
              title={t("navEditBtn")} data-testid="nav-edit-toggle">
              <Pencil className="w-3 h-3" /> {editNav ? t("navEditDone") : t("navEditBtn")}
            </button>
          </div>
        )}
        {orderedEntries.map((en) => (
          <div key={en.key} data-navkey={en.key}
            onPointerDown={(e) => startPress(en.key, e)}
            onPointerMove={checkMove}
            onPointerUp={cancelPress}
            onPointerLeave={cancelPress}
            onClickCapture={(e) => { if (suppressClick.current || dragKey) { e.preventDefault(); e.stopPropagation(); } }}
            className={`select-none flex items-stretch ${dragKey === en.key ? "opacity-40 scale-[0.98]" : ""} ${overKey === en.key && dragKey && dragKey !== en.key ? "outline outline-2 outline-[#C5A880] rounded-xl" : ""} ${dragKey ? "cursor-grabbing" : ""}`}
            data-testid={`nav-entry-${en.key.replace(/[/:]/g, "-")}`}>
            <div className="flex-1 min-w-0">{en.type === "item" ? renderItem(en.it) : en.type === "dept" ? renderDeptGroup(en.d) : renderAziendaGroup()}</div>
            {moveArrows(en.key)}
          </div>
        ))}
        {customMenu.map((s, si) => {
          const open = cmOpen[s.id] !== false;
          const curUrl = loc.pathname + loc.search;
          return (
            <div key={s.id} className="flex flex-col gap-1" data-testid={`nav-cm-section-${s.id}`}>
              <div onClick={() => toggleCm(s.id)} title={s.title}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-colors ${collapsed ? "justify-center" : ""} ${open ? "bg-white/10 text-white font-semibold" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                <Star className="w-4 h-4 shrink-0 text-[#C5A880]" />
                {!collapsed && <span className="flex-1 text-left truncate">{s.title}</span>}
                {!collapsed && editNav && !restricted && (
                  <span className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => saveCM(moveArr(customMenu, si, -1))} className="p-0.5 rounded hover:bg-white/15 text-white/40 hover:text-white" data-testid={`cm-sec-up-${s.id}`}><ChevronUp className="w-3 h-3" /></button>
                    <button onClick={() => renameCmSection(s)} className="p-0.5 rounded hover:bg-white/15 text-white/40 hover:text-[#C5A880]" title={t("navRenameTip")} data-testid={`cm-sec-ren-${s.id}`}><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => delCmSection(s)} className="p-0.5 rounded hover:bg-white/15 text-white/40 hover:text-red-400" data-testid={`cm-sec-del-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                    <button onClick={() => saveCM(moveArr(customMenu, si, 1))} className="p-0.5 rounded hover:bg-white/15 text-white/40 hover:text-white" data-testid={`cm-sec-down-${s.id}`}><ChevronDown className="w-3 h-3" /></button>
                  </span>
                )}
                {!collapsed && (open ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />)}
              </div>
              {open && (
                <div className={`flex flex-col gap-1 ${collapsed ? "" : "ml-3 pl-2 border-l border-white/10"}`} data-testid={`nav-cm-items-${s.id}`}>
                  {(s.items || []).filter((it) => {
                    if (!restricted) return true;
                    const sec = PATH_SECTION[(it.to || "").split("?")[0]];
                    return !sec || user.allowed_sections.includes(sec);
                  }).map((it, ii) => {
                    const Icon = CM_ICON[it.ttype] || Link2;
                    const active = curUrl === it.to || loc.pathname === it.to;
                    return (
                      <div key={it.id} className="flex items-stretch">
                        <Link to={it.to} onClick={() => onNavigate?.()} title={it.tname && it.tname !== it.label ? `${it.label} → ${it.tname}` : it.label} data-testid={`nav-cm-item-${it.id}`}
                          className={`flex-1 min-w-0 relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${collapsed ? "justify-center" : ""} ${active ? "bg-white/10 text-white font-semibold shadow-[inset_2px_0_0_#C5A880]" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
                          <Icon className="w-4 h-4 shrink-0" />
                          {!collapsed && <span className="truncate">{it.label}</span>}
                        </Link>
                        {!collapsed && editNav && !restricted && (
                          <div className="flex flex-col justify-center shrink-0">
                            <button onClick={() => saveCM(cmMap(s.id, (arr) => moveArr(arr, ii, -1)))} className="text-white/25 hover:text-white p-0.5 leading-none" data-testid={`cm-item-up-${it.id}`}><ChevronUp className="w-3 h-3" /></button>
                            <button onClick={() => setCmModal({ sectionId: s.id, item: it })} className="text-white/25 hover:text-[#C5A880] p-0.5 leading-none" title={t("cmEditItem")} data-testid={`cm-item-edit-${it.id}`}><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => delCmItem(s.id, it)} className="text-white/25 hover:text-red-400 p-0.5 leading-none" data-testid={`cm-item-del-${it.id}`}><Trash2 className="w-3 h-3" /></button>
                            <button onClick={() => saveCM(cmMap(s.id, (arr) => moveArr(arr, ii, 1)))} className="text-white/25 hover:text-white p-0.5 leading-none" data-testid={`cm-item-down-${it.id}`}><ChevronDown className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!collapsed && !restricted && (
                    <button onClick={() => setCmModal({ sectionId: s.id, item: null })} data-testid={`cm-add-item-${s.id}`}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] text-white/35 hover:text-white/70 transition-colors">
                      <Plus className="w-3 h-3" /> {t("cmAddItem")}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {!restricted && !collapsed && (
          <button onClick={addCmSection} data-testid="nav-cm-add-section"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider text-white/35 hover:text-white/70 transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t("cmNewSection")}
          </button>
        )}
        {netLinks.length > 0 && (
          <div className="mt-1 flex flex-col gap-1" data-testid="nav-network-section">
            {netLinks.map((l) => (
              <button key={l.id} onClick={() => setNetShop(l)} title={t("netNavCatalog").replace("{name}", l.other_name || "")}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left w-full text-sky-300/90 hover:bg-white/5 hover:text-sky-200 ${collapsed ? "justify-center" : ""}`}
                data-testid={`nav-network-${l.id}`}>
                <ShoppingCart className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="flex-1 truncate">{t("netNavCatalog").replace("{name}", l.other_name || "")}</span>}
              </button>
            ))}
          </div>
        )}
        {!restricted && !collapsed && hiddenNav.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/10" data-testid="nav-archive-section">
            <button onClick={() => setArchOpen(!archOpen)} data-testid="nav-archive-toggle"
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider text-white/35 hover:text-white/70 transition-colors">
              <FolderArchive className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left">{t("navArchTitle")} ({hiddenNav.length})</span>
              {archOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {archOpen && hiddenNav.map((k) => {
              let it = baseItems.find((b) => b.to === k);
              if (!it && k.includes("@")) {
                const base = baseItems.find((b) => b.to === k.split("@")[0]);
                if (base) it = { icon: base.icon, label: navLabels[k] || xDefaultLabel(k) };
              }
              if (!it && k.startsWith("group:")) {
                const gid = k.slice(6);
                const lbl = gid === "azienda" ? t("aziendaGroupLbl") : (departments.find((d) => d.id === gid)?.name || gid);
                it = { icon: FolderArchive, label: lbl };
              }
              if (!it && k.startsWith("ds:")) {
                const [, gid, tab] = k.split(":");
                const km = { order: "dsOrderNav", stock: "dsStockNav", prices: "dsPricesNav", docs: "dsDocsNav", eod: "dsEodNav" }[tab];
                it = { icon: Warehouse, label: `${km ? t(km) : tab} — ${deptNameOf(gid)}` };
              }
              if (!it) return null;
              return (
                <div key={k} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-white/40" data-testid={`nav-arch-item-${k.replace(/[/:]/g, "-")}`}>
                  <it.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 truncate">{it.label}</span>
                  <button onClick={() => restoreNavItem(k)} className="p-1 rounded hover:bg-white/15 text-emerald-400" title={t("navArchRestore")} data-testid={`nav-arch-restore-${k.replace(/[/:]/g, "-")}`}>
                    <ArchiveRestore className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {!restricted && !collapsed && deptItems.length > 0 && (
          <button onClick={addDept} data-testid="nav-dept-add"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider text-white/35 hover:text-white/70 transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t("deptNewBtn")}
          </button>
        )}
      </nav>

      {/* Collapsible footer: Support & Settings + Logout */}
      <div className="p-3 border-t border-white/10 text-white [&_svg]:text-white/50">
        {!collapsed && !supportOpen && (
          <div className="px-2 pb-2" data-testid="staff-push-row"><PushToggle /></div>
        )}
        {supportOpen && !collapsed && (
          <div className="flex flex-col gap-3 px-2 pb-3 mb-2 border-b border-white/10" data-testid="sidebar-support-panel">
            <ReportProblem />
            <ReportProblem kind="help" />
            <PushToggle />
          </div>
        )}
        <div className={`flex items-center gap-1 ${collapsed ? "flex-col" : "justify-between"}`}>
          <button
            onClick={() => { if (collapsed) { setCollapsed(false); localStorage.setItem("sidebar_collapsed", "0"); setSupportOpen(true); } else setSupportOpen(!supportOpen); }}
            className={`flex items-center gap-2 text-xs px-2 py-2 rounded-lg transition-colors ${supportOpen ? "text-[#C5A880] bg-white/5" : "text-white/60 hover:text-white hover:bg-white/10"}`}
            title={t("supportSettings")}
            data-testid="sidebar-support-toggle"
          >
            <Settings className="w-4 h-4" />
            {!collapsed && <span>{t("supportSettings")}</span>}
            {!collapsed && (supportOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />)}
          </button>
          <button
            data-testid="btn-logout"
            onClick={() => { logout(); nav("/"); }}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white px-2 py-2 rounded-lg hover:bg-white/10 transition-colors"
            title={t("logout")}
          >
            <LogOut className="w-4 h-4" /> {!collapsed && t("logout")}
          </button>
        </div>
      </div>
      {elevOpen && <ElevateModal onClose={() => setElevOpen(false)} />}
      {netShop && <NetworkShopModal link={netShop} t={t} onClose={() => setNetShop(null)} />}
      {cmModal && <CustomMenuModal pages={baseItems.map(({ to, label }) => ({ to, label }))} sectionId={cmModal.sectionId} item={cmModal.item} onSave={onCmSave} onClose={() => setCmModal(null)} />}
    </aside>
  );
}
